import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Api, ApiError, type TeamSummaryRow } from '../services/api'
import { ConditionalFormatProvider } from '../components/ConditionalFormatContext'
import { DataTableCard } from '../components/DataTableCard'
import { DataFreshness } from '../components/DataFreshness'
import { FormattedTableCell } from '../components/FormattedTableCell'
import { PageScrollLayout } from '../components/PageScrollLayout'
import { nextSortDirection, SortableTableHeader, type SortDirection } from '../components/SortableTableHeader'
import { type DivisionFilter, type LeagueFilter, standingsRowPassesFilters } from '../utils/mlbRegion'
import { STANDINGS_COLUMN_SCALES, STANDINGS_HEATMAP_COLUMNS } from '../utils/tableHeatmapScales'

const REFETCH_KEYS: ReadonlyArray<readonly unknown[]> = [
  ['standings'],
  ['ratings'],
  ['datasets'],
]

type SortKey =
  | 'Team'
  | 'League'
  | 'Division'
  | 'Rating'
  | 'Win'
  | 'Loss'
  | 'Most Regular Season Wins %'
  | 'Fewest Regular Season Wins %'
  | 'Playoff %'
  | 'Division %'
  | 'Pennant %'
  | 'WS %'

const DIVISION_OPTIONS: { value: DivisionFilter; label: string }[] = [
  { value: 'all', label: 'All divisions' },
  { value: 'East', label: 'East' },
  { value: 'Central', label: 'Central' },
  { value: 'West', label: 'West' },
]

export function StandingsPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['standings'],
    queryFn: Api.standings,
  })
  const [sortKey, setSortKey] = useState<SortKey>('Win')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [league, setLeague] = useState<LeagueFilter>('all')
  const [division, setDivision] = useState<DivisionFilter>('all')

  const rows = useMemo(() => {
    if (!data) return []
    const filtered = data.filter((r) => standingsRowPassesFilters(r, league, division))
    return sortRows(filtered, sortKey, sortDirection)
  }, [data, league, division, sortDirection, sortKey])

  const getHeatmapValue = useCallback((row: TeamSummaryRow, key: string) => row[key as keyof TeamSummaryRow], [])

  const onSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDirection(nextSortDirection)
      return
    }
    setSortKey(nextKey)
    setSortDirection(defaultSortDirection(nextKey))
  }

  const headerExtra = (
    <>
      <label className="flex items-center gap-2 text-sm">
        <span className="text-slate-500 whitespace-nowrap">League</span>
        <select
          className="rounded border border-slate-300 px-2 py-1"
          value={league}
          onChange={(e) => setLeague(e.target.value as LeagueFilter)}
          aria-label="Filter by league"
        >
          <option value="all">All teams</option>
          <option value="AL">American League</option>
          <option value="NL">National League</option>
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <span className="text-slate-500 whitespace-nowrap">Division</span>
        <select
          className="rounded border border-slate-300 px-2 py-1"
          value={division}
          onChange={(e) => setDivision(e.target.value as DivisionFilter)}
          aria-label="Filter by division"
        >
          {DIVISION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </>
  )

  return (
    <PageScrollLayout
      fillViewport
      title="Simulated standings"
      subtitle="Mean record + post-season probabilities across all simulated seasons."
      headerExtra={headerExtra}
      actions={<DataFreshness refetchKeys={REFETCH_KEYS} />}
    >
      {isLoading && <p className="shrink-0 px-8 pt-3 text-slate-500">Loading…</p>}
      {isError && (
        <div className="mx-8 mt-3 shrink-0 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          <p className="font-medium">Could not load standings.</p>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-rose-800">
            {error instanceof ApiError ? error.message : String(error)}
          </pre>
          {error instanceof ApiError && error.status === 401 && (
            <p className="mt-2 text-xs text-slate-600">
              Sign in expired or rejected — try signing out and back in.
            </p>
          )}
          {error instanceof ApiError && error.status === 404 && (
            <p className="mt-2 text-xs text-slate-600">
              The API could not read <code className="rounded bg-white px-1">simulation/team_summary.parquet</code>{' '}
              under latest. Confirm the ECS pipeline finished successfully and S3 has{' '}
              <code className="rounded bg-white px-1">mlb-futures/latest/simulation/</code>.
            </p>
          )}
        </div>
      )}
      {data && rows.length > 0 && (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <ConditionalFormatProvider
            rows={rows}
            columnKeys={STANDINGS_HEATMAP_COLUMNS}
            columnScales={STANDINGS_COLUMN_SCALES}
            getValue={getHeatmapValue}
          >
            <DataTableCard embedded fillViewport caption={`${rows.length} teams`}>
            <table className="table">
              <thead>
                <tr>
                  <SortableTableHeader label="Team" sortKey="Team" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="League" sortKey="League" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Division" sortKey="Division" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Rating" sortKey="Rating" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Wins" sortKey="Win" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Losses" sortKey="Loss" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Most W%" sortKey="Most Regular Season Wins %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Fewest W%" sortKey="Fewest Regular Season Wins %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Playoff %" sortKey="Playoff %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Division %" sortKey="Division %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Pennant %" sortKey="Pennant %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="WS %" sortKey="WS %" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.Team}>
                    <td className="font-medium">{String(row.Team ?? '—')}</td>
                    <td>{String(row.League ?? '—')}</td>
                    <td>{String(row.Division ?? '—')}</td>
                    <FormattedTableCell columnKey="Rating" value={row.Rating} className="font-mono">
                      {fmt3(row.Rating)}
                    </FormattedTableCell>
                    <td className="font-mono">{fmt1(row.Win)}</td>
                    <td className="font-mono">{fmt1(row.Loss)}</td>
                    <FormattedTableCell columnKey="Most Regular Season Wins %" value={row['Most Regular Season Wins %']}>
                      {pct(row['Most Regular Season Wins %'])}
                    </FormattedTableCell>
                    <FormattedTableCell columnKey="Fewest Regular Season Wins %" value={row['Fewest Regular Season Wins %']}>
                      {pct(row['Fewest Regular Season Wins %'])}
                    </FormattedTableCell>
                    <FormattedTableCell columnKey="Playoff %" value={row['Playoff %']}>
                      {pct(row['Playoff %'])}
                    </FormattedTableCell>
                    <FormattedTableCell columnKey="Division %" value={row['Division %']}>
                      {pct(row['Division %'])}
                    </FormattedTableCell>
                    <FormattedTableCell columnKey="Pennant %" value={row['Pennant %']}>
                      {pct(row['Pennant %'])}
                    </FormattedTableCell>
                    <FormattedTableCell columnKey="WS %" value={row['WS %']}>
                      {pct(row['WS %'])}
                    </FormattedTableCell>
                  </tr>
                ))}
              </tbody>
            </table>
            </DataTableCard>
          </ConditionalFormatProvider>
        </div>
      )}
      {data && rows.length === 0 && (
        <p className="text-sm text-slate-500">No teams match the selected league and division filters.</p>
      )}
    </PageScrollLayout>
  )
}

function sortRows(rows: TeamSummaryRow[], key: SortKey, direction: SortDirection): TeamSummaryRow[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const primary = compareSortValue(a[key], b[key])
    if (primary !== 0) return primary * multiplier
    return String(a.Team).localeCompare(String(b.Team))
  })
}

function defaultSortDirection(key: SortKey): SortDirection {
  return key === 'Team' || key === 'League' || key === 'Division' ? 'asc' : 'desc'
}

function compareSortValue(a: unknown, b: unknown): number {
  if (typeof a === 'number' && Number.isFinite(a) && typeof b === 'number' && Number.isFinite(b)) {
    return a - b
  }
  return String(a ?? '').localeCompare(String(b ?? ''))
}

function pct(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(2)}%` : '—'
}

function fmt3(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(3) : '—'
}

function fmt1(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(1) : '—'
}
